import { useState, useEffect } from "react";
import moment from "moment";

import MUIButton from "@mui/material/Button";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import MUIAlert from "@mui/material/Alert";

import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import MUILink from "@mui/material/Link";
import MUITextField from "@mui/material/TextField";
import MUIGrid from "@mui/material/Grid2";
import MUICircularProgress from "@mui/material/CircularProgress";
import MUITableContainer from "@mui/material/TableContainer";
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";

import Dialog from "../DialogFunctional";
import InfoItem from "../InfoItem";
import Calendar from "../../Utility/Calendar";

const DEFAULT_FOLLOWUP_DAYS = 5;
const MAX_FOLLOWUP_DAYS = 15;

export default function DialogUpdateBrightreeStopHold({
  open,
  onClose,
  row,
  onConfirm,
  brightreeStopHoldNotes = [],
  loading = false,
  orderActivity,
}) {
  const [noteText, setNoteText] = useState("");
  const [followupDays, setFollowupDays] = useState(DEFAULT_FOLLOWUP_DAYS);
  const selectedBrightreeStopHoldOrderActivityId =
    row?.["brightree_stop_hold.order_activity.id"];
  const hasSelectedBrightreeStopHoldOrderActivityId =
    selectedBrightreeStopHoldOrderActivityId !== null &&
    selectedBrightreeStopHoldOrderActivityId !== undefined &&
    selectedBrightreeStopHoldOrderActivityId !== "";
  const canCreateBrightreeStopHoldNote =
    hasSelectedBrightreeStopHoldOrderActivityId && Boolean(orderActivity);
  const isWaitingForSelectedBrightreeStopHoldOrderActivity =
    hasSelectedBrightreeStopHoldOrderActivityId && !orderActivity;

  useEffect(() => {
    if (!open) {
      setNoteText("");
      setFollowupDays(DEFAULT_FOLLOWUP_DAYS);
    }
  }, [open]);

  /**
   * Render header title of dialog
   */
  function renderHeader() {
    return (
      <MUITypography variant="h6" color="textPrimary">
        Update Stop/Hold
      </MUITypography>
    );
  }

  function renderDialogLinksAndStopHoldDetails() {
    const stopHoldTypeTermId =
      row["brightree_stop_hold.brightree_stop_hold_type_term_id"];
    const stopHoldReasonTermId =
      row["brightree_stop_hold.brightree_stop_hold_reason_term_id"];
    const selectedBrightreeStopHoldSalesOrderId =
      row["brightree_stop_hold.sales_order_id"];
    const stopHoldTypeDisplayName =
      stopHoldTypeTermId === null || stopHoldTypeTermId === undefined
        ? "N/A"
        : stopHoldTypeTermId;
    const stopHoldReasonDisplayName =
      stopHoldReasonTermId === null || stopHoldReasonTermId === undefined
        ? "N/A"
        : stopHoldReasonTermId;

    return (
      <MUIGrid size={12}>
        <MUIGrid container spacing={1.5}>
          <MUIGrid size={12}>
            <MUIGrid container spacing={3}>
              {orderActivity ? (
                <MUIGrid>
                  <MUILink
                    href="#"
                    color="primary"
                    sx={{
                      alignItems: "center",
                      display: "inline-flex",
                      fontWeight: 700,
                      gap: 0.75,
                      textDecoration: "none",
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      window.open(
                        `/chart/${orderActivity.patientId}/order/${orderActivity.id}`,
                        "_blank",
                      );
                    }}
                  >
                    {`Order #${orderActivity.id}`}
                    <MUIOpenInNewIcon fontSize="small" />
                  </MUILink>
                </MUIGrid>
              ) : null}

              {selectedBrightreeStopHoldSalesOrderId ? (
                <MUIGrid>
                  <MUILink
                    href={`https://brightree.net/F1/02200/OrbitMedical/OrderEntry/frmSOOrderRO.aspx?SalesOrderKey=${selectedBrightreeStopHoldSalesOrderId}`}
                    target="_blank"
                    rel="noreferrer"
                    color="primary"
                    sx={{
                      alignItems: "center",
                      display: "inline-flex",
                      fontWeight: 700,
                      gap: 0.75,
                      textDecoration: "none",
                    }}
                  >
                    {`Brightree ID #${selectedBrightreeStopHoldSalesOrderId}`}
                    <MUIOpenInNewIcon fontSize="small" />
                  </MUILink>
                </MUIGrid>
              ) : null}
            </MUIGrid>
          </MUIGrid>

          <MUIGrid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <MUIGrid
              sx={{
                backgroundColor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                padding: 1.25,
              }}
            >
              <InfoItem
                label="Stop/Hold Type"
                value={stopHoldTypeDisplayName}
              />
            </MUIGrid>
          </MUIGrid>

          <MUIGrid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <MUIGrid
              sx={{
                backgroundColor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                padding: 1.25,
              }}
            >
              <InfoItem
                label="Stop/Hold Reason"
                value={stopHoldReasonDisplayName}
              />
            </MUIGrid>
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  }

  /**
   * Render the existing brightree stop/hold notes for this order, sorted by
   * creation date with the most recent first.
   * @returns {React.ReactNode|null}
   */
  function renderBrightreeStopHoldNotes() {
    if (!brightreeStopHoldNotes || brightreeStopHoldNotes.length === 0) {
      return null;
    }

    const sortedBrightreeStopHoldNotes = [...brightreeStopHoldNotes].sort(
      (firstNote, secondNote) =>
        new Date(secondNote.createdAt) - new Date(firstNote.createdAt),
    );

    return (
      <MUIGrid size={12}>
        <MUIGrid
          container
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 1,
          }}
        >
          <MUIGrid sx={{ paddingBottom: 0 }}>
            <MUITypography
              variant="subtitle1"
              sx={{ fontWeight: 500, display: "block" }}
            >
              Brightree Stop/Hold Notes
            </MUITypography>
          </MUIGrid>
          <MUIGrid>
            <MUITypography
              variant="caption"
              sx={{
                color: "text.primary",
                backgroundColor: "background.default",
                borderRadius: 999,
                display: "inline-flex",
                fontWeight: 600,
                paddingX: 1.25,
                paddingY: 0.5,
              }}
            >
              {`${sortedBrightreeStopHoldNotes.length} ${sortedBrightreeStopHoldNotes.length === 1 ? "Note" : "Notes"}`}
            </MUITypography>
          </MUIGrid>
        </MUIGrid>
        <MUITableContainer
          sx={{
            maxHeight: 300,
            border: 1,
            borderColor: "divider",
          }}
        >
          <MUITable stickyHeader size="small">
            <MUITableBody
              sx={{
                "& .MuiTableRow-root:nth-of-type(odd) .MuiTableCell-root": {
                  backgroundColor: "background.default",
                },
                "& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root": {
                  backgroundColor: "background.paper",
                },
              }}
            >
              {sortedBrightreeStopHoldNotes.map(
                (brightreeStopHoldNote, brightreeStopHoldNoteIndex) => {
                  const createdByDisplayName = brightreeStopHoldNote.createdBy
                    ? `${brightreeStopHoldNote.createdBy.firstname} ${brightreeStopHoldNote.createdBy.lastname}`
                    : "Unknown";

                  return (
                    <MUITableRow
                      key={
                        brightreeStopHoldNote.id ?? brightreeStopHoldNoteIndex
                      }
                    >
                      <MUITableCell
                        sx={{
                          maxWidth: "180px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          verticalAlign: "top",
                          whiteSpace: "nowrap",
                          width: "180px",
                        }}
                      >
                        {moment(brightreeStopHoldNote.createdAt).format(
                          "M/D/YYYY h:mma",
                        )}
                        <MUITypography
                          variant="caption"
                          component="div"
                          sx={{
                            color: "text.secondary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {createdByDisplayName}
                        </MUITypography>
                      </MUITableCell>
                      <MUITableCell
                        sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                      >
                        {brightreeStopHoldNote.text}
                      </MUITableCell>
                    </MUITableRow>
                  );
                },
              )}
            </MUITableBody>
          </MUITable>
        </MUITableContainer>
      </MUIGrid>
    );
  }

  function renderCreateNoteTextField() {
    if (!canCreateBrightreeStopHoldNote) {
      return null;
    }

    const hasExistingBrightreeStopHoldNotes =
      brightreeStopHoldNotes && brightreeStopHoldNotes.length > 0;

    const handleNoteTextChange = (updatedFields) => {
      setNoteText(updatedFields.noteText);
    };

    return (
      <MUIGrid size={12}>
        <MUIGrid
          sx={{
            paddingTop: hasExistingBrightreeStopHoldNotes ? 1 : 0,
            paddingX: 1,
          }}
        >
          <MUITextField
            multiline
            rows={3}
            fullWidth={true}
            label={"Note"}
            value={noteText ? noteText : ""}
            onChange={(e) => handleNoteTextChange({ noteText: e.target.value })}
            variant="standard"
            required={canCreateBrightreeStopHoldNote}
            slotProps={{
              htmlInput: { style: { resize: "both" } },
              inputLabel: { shrink: true },
            }}
          />
        </MUIGrid>
      </MUIGrid>
    );
  }

  function renderFollowupDateField() {
    return (
      <MUIGrid size={12}>
        <MUIGrid
          container
          spacing={1}
          sx={{
            alignItems: "center",
            paddingX: 1,
          }}
        >
          <MUIGrid sx={{ width: "100px" }}>
            <MUITypography variant="body1">Follow up in</MUITypography>
          </MUIGrid>
          <MUIGrid sx={{ width: "50px" }}>
            <MUITextField
              type="number"
              size="small"
              variant="standard"
              value={followupDays}
              onChange={(event) => {
                if (
                  event.target.value > MAX_FOLLOWUP_DAYS ||
                  event.target.value < 0
                ) {
                  return;
                }
                setFollowupDays(Number(event.target.value));
              }}
              fullWidth
              slotProps={{
                htmlInput: { min: 0, max: MAX_FOLLOWUP_DAYS },
                inputLabel: { shrink: true },
              }}
            />
          </MUIGrid>
          <MUIGrid>
            <MUITypography variant="body1">
              business days (
              {moment(
                Calendar.addDays({
                  daysToAdd: followupDays,
                  useBusinessDays: true,
                }),
              ).format("ddd, MMM D")}
              )
            </MUITypography>
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  }

  function renderNextWorkDateAlert() {
    const today = moment().startOf("day");
    const brightreeStopHoldFollowupDate = moment(
      row?.["brightree_stop_hold.followup_date"],
    ).startOf("day");

    if (!brightreeStopHoldFollowupDate) {
      return null;
    }

    if (!brightreeStopHoldFollowupDate.isAfter(today, "day")) {
      return null;
    }

    const daysAway = brightreeStopHoldFollowupDate.diff(today, "days");
    const dayLabel = daysAway === 1 ? "day" : "days";
    const formattedDate = brightreeStopHoldFollowupDate.format("MMMM D, YYYY");
    const label = "Next followup date";

    return (
      <MUIGrid size={12}>
        <MUIAlert severity="info">
          {`${label}: ${formattedDate} (${daysAway} ${dayLabel})`}
        </MUIAlert>
      </MUIGrid>
    );
  }

  /**
   * Render main content text of dialog
   */
  function renderContent() {
    if (!row) {
      return (
        <MUIGrid
          container
          sx={{
            justifyContent: "center",
            py: 4,
          }}
        >
          <MUICircularProgress />
        </MUIGrid>
      );
    }

    if (isWaitingForSelectedBrightreeStopHoldOrderActivity) {
      return (
        <MUIGrid
          container
          sx={{
            justifyContent: "center",
            py: 4,
          }}
        >
          <MUICircularProgress />
        </MUIGrid>
      );
    }

    return (
      <MUIGrid container spacing={3} rowSpacing={2.5}>
        {renderDialogLinksAndStopHoldDetails()}
        {renderBrightreeStopHoldNotes()}
        {renderCreateNoteTextField()}
        {renderFollowupDateField()}
        {renderNextWorkDateAlert()}
      </MUIGrid>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  function renderActions() {
    return (
      <MUIDialogActions spacing={2}>
        <MUIButton
          variant="text"
          color="error"
          disabled={loading}
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          loading={loading}
          disabled={
            !row ||
            isWaitingForSelectedBrightreeStopHoldOrderActivity ||
            (canCreateBrightreeStopHoldNote && !noteText.trim())
          }
          onClick={() => {
            onConfirm(noteText, followupDays);
          }}
        >
          Update Stop/Hold
        </MUIButton>
      </MUIDialogActions>
    );
  }

  /**
   * Gets the max width of the dialog.
   * @returns {string}
   */
  function getMaxWidth() {
    return "400px";
  }

  return (
    <Dialog
      renderHeader={renderHeader}
      renderContent={renderContent}
      renderActions={renderActions}
      maxWidth={getMaxWidth}
      open={open}
      onClose={onClose}
    />
  );
}
